import React, { useEffect, useRef } from "react";

import { useGql } from "../Core/gqlHooks";
import { queryPageAbout } from "../Core/services";
import PageLayout from "../Components/pageLayout/PageLayout";
import PageSubHeader from "../Components/pageSubHeader/PageSubHeader";
import { mediaUrl, usePageSeo } from "../Core/utils";

import "./APropos.scss";

type CarouselImage = { attributes: { hash: string; ext: string } };

/** Pixels per second */
const AUTO_SCROLL_SPEED = 40;
const COPIES = 2;

const AboutCarousel = ({ images }: { images: CarouselImage[] }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const setRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(0);
  const dragRef = useRef({
    active: false,
    startX: 0,
    startOffset: 0,
  });

  useEffect(() => {
    const track = trackRef.current;
    const set = setRef.current;
    if (!track || !set || images.length === 0) {
      return undefined;
    }

    let frameId = 0;
    let lastTime = performance.now();

    const wrap = (value: number) => {
      const width = set.offsetWidth;
      if (width <= 0) return value;
      const wrapped = value % width;
      return wrapped < 0 ? wrapped + width : wrapped;
    };

    const render = () => {
      track.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
    };

    const tick = (now: number) => {
      const dt = Math.min(now - lastTime, 64);
      lastTime = now;

      if (!dragRef.current.active) {
        offsetRef.current = wrap(
          offsetRef.current + AUTO_SCROLL_SPEED * (dt / 1000)
        );
        render();
      }

      frameId = requestAnimationFrame(tick);
    };

    render();
    frameId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [images]);

  function onPointerDown(event: React.PointerEvent<HTMLDivElement>) {
    const track = trackRef.current;
    if (!track) return;

    dragRef.current = {
      active: true,
      startX: event.clientX,
      startOffset: offsetRef.current,
    };
    track.setPointerCapture(event.pointerId);
    track.classList.add("is-dragging");
  }

  function onPointerMove(event: React.PointerEvent<HTMLDivElement>) {
    const track = trackRef.current;
    const set = setRef.current;
    const drag = dragRef.current;
    if (!track || !set || !drag.active) return;

    const width = set.offsetWidth;
    let next = drag.startOffset - (event.clientX - drag.startX);
    if (width > 0) {
      next %= width;
      if (next < 0) next += width;
    }
    offsetRef.current = next;
    track.style.transform = `translate3d(${-next}px, 0, 0)`;
  }

  function endDrag(event: React.PointerEvent<HTMLDivElement>) {
    const track = trackRef.current;
    if (!track || !dragRef.current.active) return;

    dragRef.current.active = false;
    track.releasePointerCapture(event.pointerId);
    track.classList.remove("is-dragging");
  }

  return (
    <div className="carousel-wrapper">
      <div
        className="carousel-track"
        ref={trackRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {Array.from({ length: COPIES }, (_, copy) => (
          <div
            className="carousel-set"
            ref={copy === 0 ? setRef : undefined}
            key={copy}
            aria-hidden={copy > 0 || undefined}
          >
            {images.map((image) => (
              <div
                className="carousel-slide"
                key={`${copy}-${image.attributes.hash}`}
              >
                <div
                  className="carousel-image"
                  style={{
                    backgroundImage: `url(${mediaUrl(
                      image.attributes.hash,
                      image.attributes.ext
                    )})`,
                  }}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const APropos = () => {
  const { data, loading } = useGql({
    query: queryPageAbout,
  });

  const page = data?.pageAbout?.data?.attributes;
  const carousel = page?.carousel?.data ?? [];

  usePageSeo({
    title: page?.pageTitle,
    keywords: page?.keywords,
  });

  return (
    <PageLayout
      className="apropos-container"
      contentClassName="apropos"
      title="À Propos"
      after={
        !loading && carousel.length > 0 && <AboutCarousel images={carousel} />
      }
    >
      {!loading && <PageSubHeader title={page?.title} intro={page?.intro} />}
    </PageLayout>
  );
};

export default APropos;
